import { makeAutoObservable } from "mobx";
import { createClient, createDurationSec, localSave } from "@/core/helpers";
import { ApiClient } from "@/clients/ApiClient";

const currentEpisode: Episode = {
    art: "",
    content: "",
    created_at: "",
    explicit: 0,
    guid: "",
    link: "",
    podcast_id: 0,
    season: 0,
    snippet: "",
    type: "",
    updated_at: "",
    id: -1,
    sound: "",
    title: "",
    length: "0",
    duration: 0,
    slug: "",
    published: "",
};

const currentAuthor: Author = {
    art: "",
    content: "",
    copyright: "",
    created_at: "",
    id: 0,
    name: "",
    slug: "",
    updated_at: "",
    url: "",
};

const currentPodcast: Podcast = {
    author: currentAuthor,
    blurhash: "",
    categories: [],
    created_at: "",
    description: "",
    feed: "",
    itunes_id: 0,
    release_date: "",
    summary: "",
    updated_at: "",
    url: "",
    website: "",
    name: "",
    slug: "",
    art: "",
    id: -1,
};

const defaultState = {
    currentEpisode,
    currentPodcast,
};

const PLAYER_LOCAL_STORAGE_KEY = "hp_player_session";

export class PlayerStore {
    isPlaying = false;
    active = false;
    currentEpisode: Episode = defaultState.currentEpisode;
    currentPodcast: Podcast = defaultState.currentPodcast;
    private client: ApiClient;

    constructor() {
        makeAutoObservable(this);
        this.client = createClient();
    }

    get seconds() {
        return createDurationSec(this.currentEpisode.length);
    }

    setCurrentEpisode(episode: Episode, podcast: Podcast) {
        this.currentEpisode = episode;
        this.currentPodcast = podcast;
        this.persistSession();
        this.saveToHistory(podcast, episode);
    }

    setPlayingState(isPlaying: boolean) {
        this.isPlaying = isPlaying;
    }

    restoreSession(episode: Episode, podcast: Podcast) {
        this.currentEpisode = episode;
        this.currentPodcast = podcast;
        this.active = true;
        this.isPlaying = false;
    }

    restorePersistedSession() {
        if (typeof window === "undefined") {
            return;
        }

        try {
            const raw = window.localStorage.getItem(PLAYER_LOCAL_STORAGE_KEY);
            if (!raw) {
                return;
            }

            const parsed = JSON.parse(raw) as {
                episode?: Episode;
                podcast?: Podcast;
            };

            if (!parsed.episode || !parsed.podcast) {
                return;
            }

            this.restoreSession(parsed.episode, parsed.podcast);
        } catch {
            window.localStorage.removeItem(PLAYER_LOCAL_STORAGE_KEY);
        }
    }

    persistSession() {
        if (typeof window === "undefined") {
            return;
        }

        try {
            window.localStorage.setItem(
                PLAYER_LOCAL_STORAGE_KEY,
                JSON.stringify({
                    episode: this.currentEpisode,
                    podcast: this.currentPodcast,
                })
            );
        } catch {
            try {
                window.localStorage.removeItem(PLAYER_LOCAL_STORAGE_KEY);
            } catch {
                // Ignore storage failures and keep in-memory playback state working.
            }
        }
    }

    clearPersistedSession() {
        if (typeof window === "undefined") {
            return;
        }

        try {
            window.localStorage.removeItem(PLAYER_LOCAL_STORAGE_KEY);
        } catch {
            // Ignore storage failures and keep in-memory playback state working.
        }
    }

    play(progress: number) {
        this.active = true;
        this.isPlaying = true;
        this.trackPlayerEvents("play", {
            episode: this.currentEpisode.id,
            podcast: this.currentPodcast.id,
            progress,
        }).catch((e) => {
            console.log(e);
        });
    }

    pause(progress: number) {
        this.isPlaying = false;
        this.trackPlayerEvents("pause", {
            episode: this.currentEpisode.id,
            podcast: this.currentPodcast.id,
            progress,
        }).catch((e) => {
            console.log(e);
        });
    }

    saveToHistory(podcast: Podcast, episode: Episode) {
        localSave("hp_pod_history", podcast.id, 5).catch(() => {
            console.error("Could not save podcast to history");
        });
        localSave("hp_episode_history", episode.id, 5).catch(() => {
            console.error("Could not save episode to history");
        });
    }

    async trackPlayerEvents(action: string, payload = {}) {
        if ("plausible" in window) {
            window.plausible(action);
        }
        await this.client.post(`/episodes/${this.currentEpisode.id}/action`, {
            action,
            payload,
            client: window["clientId"] ?? "",
        });
    }

    close() {
        this.isPlaying = false;
        this.active = false;
        this.currentPodcast = defaultState.currentPodcast;
        this.currentEpisode = defaultState.currentEpisode;
        this.clearPersistedSession();
    }
}
